import { apiClient } from './apiClient'

export interface MarketPrice {
  id: string
  symbol: string
  exchange: string
  current_price: number
  open_price: number
  high_price: number
  low_price: number
  close_price: number
  change_amount: number
  change_percent: number
  bid_price?: number
  ask_price?: number
  volume: number
  timestamp: number
  timestamp_tz: string
  created_at: string
  price_gram_24k?: number
  price_gram_22k?: number
  price_gram_21k?: number
  price_gram_20k?: number
  price_gram_18k?: number
  price_gram_16k?: number
  price_gram_14k?: number
  price_gram_10k?: number
}

export interface MarketStats {
  total_records: number
  symbols_count: number
  exchanges_count: number
  avg_price: number
  min_price: number
  max_price: number
  price_volatility: number
}

export interface MonitoringStatus {
  all_healthy: boolean
  alerts: Array<{
    symbol: string
    message: string
    severity: 'CRITICAL' | 'WARNING' | 'INFO'
    timestamp: string
  }>
  collection_status: Array<{
    symbol: string
    last_update: string
    seconds_since_update: number
    status: string
  }>
}

export interface HistoricalDataParams {
  symbol?: string
  exchange?: string
  start_time?: string
  end_time?: string
  limit?: number
  offset?: number
}

class MarketDataService {
  /**
   * 获取最新行情数据
   */
  async getLatestPrices(params?: { symbol?: string; exchange?: string }) {
    const queryParams = new URLSearchParams()
    if (params?.symbol) queryParams.append('symbol', params.symbol)
    if (params?.exchange) queryParams.append('exchange', params.exchange)
    queryParams.append('latest_only', 'true')

    const url = `/market-prices${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    return apiClient.get<{ prices: MarketPrice[]; total: number }>(url)
  }

  /**
   * 获取历史行情数据
   */
  async getHistoricalPrices(params: HistoricalDataParams) {
    const queryParams = new URLSearchParams()
    if (params.symbol) queryParams.append('symbol', params.symbol)
    if (params.exchange) queryParams.append('exchange', params.exchange)
    if (params.start_time) queryParams.append('start_time', params.start_time)
    if (params.end_time) queryParams.append('end_time', params.end_time)
    if (params.limit) queryParams.append('limit', params.limit.toString())
    if (params.offset) queryParams.append('offset', params.offset.toString())

    const url = `/market-prices?${queryParams.toString()}`
    return apiClient.get<{ prices: MarketPrice[]; total: number }>(url)
  }

  /**
   * 获取行情统计数据
   */
  async getMarketStats() {
    return apiClient.get<MarketStats>('/market-data-admin', {
      params: { action: 'get_stats' },
    })
  }

  /**
   * 获取监控状态
   */
  async getMonitoringStatus() {
    return apiClient.get<MonitoringStatus>('/market-data-admin', {
      params: { action: 'get_monitoring' },
    })
  }

  /**
   * 获取采集中断告警
   */
  async getCollectionInterruptions() {
    return apiClient.get('/market-data-admin', {
      params: { action: 'check_collection_interruption' },
    })
  }

  /**
   * 获取价格异常告警
   */
  async getPriceAnomalies() {
    return apiClient.get('/market-data-admin', {
      params: { action: 'check_price_anomaly' },
    })
  }

  /**
   * 获取成交量异常告警
   */
  async getVolumeAnomalies() {
    return apiClient.get('/market-data-admin', {
      params: { action: 'check_volume_anomaly' },
    })
  }

  /**
   * 手动触发数据采集
   */
  async triggerCollection(symbols?: string[]) {
    return apiClient.post('/market-data-admin', {
      action: 'trigger_collection',
      symbols,
    })
  }

  /**
   * 手动触发数据清理
   */
  async triggerCleanup(days?: number) {
    return apiClient.post('/market-data-admin', {
      action: 'trigger_cleanup',
      retention_days: days || 90,
    })
  }

  /**
   * 刷新物化视图
   */
  async refreshMaterializedView() {
    return apiClient.post('/market-data-admin', {
      action: 'refresh_materialized_view',
    })
  }

  /**
   * 获取可用的交易对列表
   */
  async getAvailableSymbols() {
    return apiClient.get<{ symbols: string[] }>('/market-symbols')
  }

  /**
   * 获取实时推送统计
   */
  async getRealtimeStats() {
    return apiClient.get('/market-realtime-push', {
      params: { action: 'get_status' },
    })
  }

  /**
   * 获取消息去重统计
   */
  async getDedupStats() {
    return apiClient.get('/market-realtime-push', {
      params: { action: 'get_dedup_stats' },
    })
  }
}

export const marketDataService = new MarketDataService()
